import React, { useMemo, useState } from 'react';
import { Avatar, Badge, Button, Dropdown, Form, Input, Layout, Menu, Modal, Space, Tag, Typography, Upload, message } from 'antd';
import {
  BankOutlined,
  CalendarOutlined,
  DashboardOutlined,
  DollarOutlined,
  LogoutOutlined,
  SettingOutlined,
  TeamOutlined,
  IdcardOutlined,
  SafetyCertificateOutlined,
  UserOutlined,
  FileTextOutlined,
  UploadOutlined,
  FormOutlined
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { fetchCurrentUser, logoutUser } from '../redux/slices/authSlice';
import userService from '../services/userService';
import { canManageRoles, canViewCTCs, canViewDocuments, canViewFeature, canViewOrganizations, getFeatureAccess, getFeatureAccessList, getRoleKey, isEmployee } from '../utils/permissions';

const { Sider, Content, Header } = Layout;

const navigationItems = [
  { key: '/dashboard', label: 'Dashboard', icon: <DashboardOutlined />, featureKey: 'dashboard' },
  { key: '/organizations', label: 'Organizations', icon: <BankOutlined />, featureKey: 'organisation', customVisible: canViewOrganizations },
  { key: '/roles', label: 'Roles', icon: <SafetyCertificateOutlined />, featureKey: 'roles', customVisible: canManageRoles },
  { key: '/leave-types', label: 'Leave Types', icon: <FileTextOutlined />, featureKey: 'roles', customVisible: canManageRoles },
  { key: '/users', label: 'Users', icon: <IdcardOutlined />, featureKey: 'users' },
  { key: '/employees', label: 'Employees', icon: <TeamOutlined />, featureKey: 'employees' },
  { key: '/ctc', label: 'CTC', icon: <DollarOutlined />, featureKey: 'ctc', customVisible: (user) => isEmployee(user) || canViewCTCs(user) },
  { key: '/documents', label: 'Documents', icon: <FileTextOutlined />, featureKey: 'documents', customVisible: (user) => isEmployee(user) || canViewDocuments(user) },
  { key: '/leaves', label: 'Leaves', icon: <CalendarOutlined />, featureKey: 'leaves' },
  { key: '/holidays', label: 'Holidays', icon: <FileTextOutlined />, featureKey: 'holidays' },
  { key: '/payslip-templates', label: 'Payslip Templates', icon: <FileTextOutlined />, featureKey: 'payroll' },
  { key: '/salary-structure', label: 'Salary Structure', icon: <FileTextOutlined />, featureKey: 'payrollStructure' },
  { key: '/tax-declaration', label: 'Tax Declaration', icon: <FormOutlined />, featureKey: 'taxDeclaration' },
  { key: '/payroll', label: 'Payroll', icon: <DollarOutlined />, featureKey: 'payroll' },
  { key: '/attendance', label: 'Attendance', icon: <UserOutlined />, featureKey: 'attendance' },
  { key: '/settings', label: 'Settings', icon: <SettingOutlined />, featureKey: 'settings' }
];

function AppShell({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [profileForm] = Form.useForm();
  const [passwordForm] = Form.useForm();

  const menuItems = useMemo(() => {
    return navigationItems
      .filter((item) => item.customVisible ? item.customVisible(user) : canViewFeature(user, item.featureKey))
      .map((item) => ({
        key: item.key,
        icon: item.icon,
        label: (
          <div className="app-shell__nav-item">
            <span>{item.label}</span>
            {/* <Tag color="blue" className="app-shell__crud-tag">
              {getFeatureAccess(user, item.featureKey)}
            </Tag> */}
          </div>
        )
      }));
  }, [user]);

  const featureAccess = useMemo(() => getFeatureAccessList(user), [user]);

  const selectedKey = useMemo(() => {
    const match = navigationItems.find((item) => location.pathname.startsWith(item.key));
    return match ? [match.key] : ['/dashboard'];
  }, [location.pathname]);

  const handleLogout = async () => {
    await dispatch(logoutUser());
    navigate('/login');
  };

  const openProfileModal = () => {
    profileForm.setFieldsValue({
      firstName: user?.firstName,
      lastName: user?.lastName,
      phone: user?.phone
    });
    setIsProfileModalOpen(true);
  };

  const handleProfileSave = async (values) => {
    try {
      setProfileSaving(true);
      await userService.updateProfile(values);
      await dispatch(fetchCurrentUser());
      message.success('Profile updated successfully');
      setIsProfileModalOpen(false);
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to update profile');
    } finally {
      setProfileSaving(false);
    }
  };

  const handlePasswordSave = async (values) => {
    try {
      setPasswordSaving(true);
      await userService.changePassword(values);
      message.success('Password changed successfully');
      passwordForm.resetFields();
      setIsPasswordModalOpen(false);
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to change password');
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleProfilePhotoUpload = async ({ file, onSuccess, onError }) => {
    try {
      await userService.uploadProfilePhoto(file);
      await dispatch(fetchCurrentUser());
      message.success('Profile photo uploaded successfully');
      onSuccess?.({}, file);
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to upload profile photo');
      onError?.(error);
    }
  };

  const profileMenuItems = [
    {
      key: 'profile',
      label: 'Update Profile',
      onClick: openProfileModal
    },
    {
      key: 'password',
      label: 'Change Password',
      onClick: () => setIsPasswordModalOpen(true)
    },
    {
      key: 'logout',
      label: 'Logout',
      onClick: handleLogout
    }
  ];

  return (
    <Layout className="app-shell">
      <Sider breakpoint="lg" collapsedWidth="0" width={320} className="app-shell__sider">
        <div className="app-shell__brand">
          <div className="app-shell__brand-mark">
            <BankOutlined />
          </div>
          <div>
            <Typography.Title level={4} className="app-shell__brand-title">HRMS SaaS</Typography.Title>
            <Typography.Text className="app-shell__brand-subtitle">Role-based access</Typography.Text>
          </div>
        </div>

        <div className="app-shell__user">
          <Typography.Text className="app-shell__user-name">
            {user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email : 'User'}
          </Typography.Text>
          <Badge color="#0ea5e9" text={(getRoleKey(user) || 'guest').toUpperCase()} />
        </div>

        <div className="app-shell__section">
          <Typography.Title level={5} className="app-shell__section-title">Navigation</Typography.Title>
          <Menu
            mode="inline"
            selectedKeys={selectedKey}
            items={menuItems}
            className="app-shell__menu"
            onClick={({ key }) => navigate(key)}
          />
        </div>

        <div className="app-shell__section app-shell__section--access">
          <Typography.Title level={5} className="app-shell__section-title">Feature Access</Typography.Title>
          <div className="app-shell__access-list">
            {featureAccess.map((feature) => (
              <div key={feature.key} className="app-shell__access-row">
                <span className="app-shell__access-label">{feature.label}</span>
                <Tag color={feature.access === '-' ? 'default' : 'geekblue'}>{feature.access}</Tag>
              </div>
            ))}
          </div>
        </div>

        <Button type="default" icon={<LogoutOutlined />} danger block className="app-shell__logout" onClick={handleLogout}>
          Logout
        </Button>
      </Sider>

      <Layout className="app-shell__main">
        <Header className="app-shell__header">
          <div className="app-shell__header-spacer" />
          <Dropdown
            menu={{ items: profileMenuItems }}
            trigger={['click']}
            placement="bottomRight"
          >
            <button type="button" className="app-shell__profile-trigger">
              <Avatar
                size={42}
                src={user?.profilePhoto || undefined}
                icon={!user?.profilePhoto ? <UserOutlined /> : undefined}
              />
              <div className="app-shell__profile-meta">
                <span className="app-shell__profile-name">
                  {user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email : 'User'}
                </span>
                <span className="app-shell__profile-role">
                  {(getRoleKey(user) || 'guest').toUpperCase()}
                </span>
              </div>
            </button>
          </Dropdown>
        </Header>
        <Content className="app-shell__content">
          {children}
        </Content>
      </Layout>

      <Modal
        title="Update Profile"
        open={isProfileModalOpen}
        onCancel={() => setIsProfileModalOpen(false)}
        onOk={() => profileForm.submit()}
        okText="Save"
        confirmLoading={profileSaving}
      >
        <Space direction="vertical" size={16} style={{ width: '100%', marginBottom: 16 }}>
          <Avatar
            size={88}
            src={user?.profilePhoto || undefined}
            icon={!user?.profilePhoto ? <UserOutlined /> : undefined}
          />
          <Upload
            accept="image/*"
            showUploadList={false}
            beforeUpload={(file) => {
              if (!file.type.startsWith('image/')) {
                message.error('Only image files are allowed');
                return Upload.LIST_IGNORE;
              }
              if (file.size > 50 * 1024) {
                message.error('Profile photo must be 50 KB or smaller');
                return Upload.LIST_IGNORE;
              }
              return true;
            }}
            customRequest={handleProfilePhotoUpload}
          >
            <Button icon={<UploadOutlined />}>Upload Profile Photo</Button>
          </Upload>
          <Typography.Text type="secondary">Maximum size: 50 KB</Typography.Text>
        </Space>
        <Form form={profileForm} layout="vertical" onFinish={handleProfileSave}>
          <Form.Item label="First Name" name="firstName" rules={[{ required: true, message: 'Please enter first name' }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Last Name" name="lastName" rules={[{ required: true, message: 'Please enter last name' }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Phone" name="phone">
            <Input />
          </Form.Item>
          <Form.Item label="Email">
            <Input value={user?.email} disabled />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Change Password"
        open={isPasswordModalOpen}
        onCancel={() => setIsPasswordModalOpen(false)}
        onOk={() => passwordForm.submit()}
        okText="Update Password"
        confirmLoading={passwordSaving}
      >
        <Form form={passwordForm} layout="vertical" onFinish={handlePasswordSave}>
          <Form.Item label="Current Password" name="currentPassword" rules={[{ required: true, message: 'Please enter current password' }]}>
            <Input.Password />
          </Form.Item>
          <Form.Item label="New Password" name="newPassword" rules={[{ required: true, message: 'Please enter new password' }]}>
            <Input.Password />
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
}

export default AppShell;
